<template>
  <div 
    ref="containerRef"
    class="lazy-wysiwyg-container"
    :class="{ 'is-loading': !isLoaded, 'is-loaded': isLoaded }"
  >
    <!-- Placeholder avant chargement -->
    <div 
      v-if="!isLoaded" 
      class="lazy-wysiwyg-placeholder"
      @click="loadEditor"
    >
      <div class="placeholder-content">
        <i class="fa fa-edit placeholder-icon"></i>
        <p class="placeholder-text">Cliquer pour éditer</p>
        <div v-if="modelValue" class="placeholder-preview">
          <div class="preview-content" v-html="truncatedContent"></div>
        </div>
      </div>
    </div>

    <!-- Éditeur WYSIWYG chargé -->
    <BaseWysiwygEditor
      v-if="isLoaded"
      ref="baseComponentRef"
      :model-value="modelValue"
      :translation="translation"
      :variables-list="variablesList"
      :content-class="contentClass"
      :content-stylesheet-href="contentStylesheetHref"
      @update:model-value="$emit('update:modelValue', $event)"
      @activate-group="$emit('activate-group', $event)"
      @deactivate-group="$emit('deactivate-group', $event)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onBeforeUnmount, ref, computed, PropType, nextTick } from 'vue';
import BaseWysiwygEditor from './BaseWysiwygEditor.vue';
import { Translation } from '@/types';
import { Variable } from '../template-builder/utils/variables-list';

export default defineComponent({
  name: 'LazyWysiwygEditor',
  
  components: {
    BaseWysiwygEditor,
  },

  props: {
    modelValue: {
      type: String,
      required: true,
    },

    translation: {
      type: Object as PropType<Translation>,
      required: false,
      default: null,
    },

    variablesList: {
      type: Array as PropType<Variable[]>,
      required: false,
      default: () => [],
    },

    contentClass: {
      type: String,
      required: false,
      default: '',
    },

    contentStylesheetHref: {
      type: String,
      required: false,
      default: '',
    },

    // Option pour forcer le chargement immédiat (utile pour debug)
    forceLoad: {
      type: Boolean,
      required: false,
      default: false,
    },

    // Mode de chargement: 'auto' (intersection), 'click' (clic uniquement), 'immediate' (immédiat)
    loadMode: {
      type: String,
      required: false,
      default: 'auto', // Par défaut, chargement au scroll
      validator: (value: string) => ['auto', 'click', 'immediate'].includes(value),
    },

    // Seuil de visibilité pour déclencher le chargement (0.1 = 10% visible)
    intersectionThreshold: {
      type: Number,
      required: false,
      default: 0.1,
    },
  },

  emits: {
    'update:modelValue': String,
    'activate-group': String,
    'deactivate-group': String,
  },

  setup(props) {
    const containerRef = ref<HTMLElement>();
    const isLoaded = ref(false);
    let observer: IntersectionObserver | null = null;

    // Contenu tronqué pour l'aperçu
    const truncatedContent = computed(() => {
      if (!props.modelValue) return '';
      const textContent = props.modelValue.replace(/<[^>]*>/g, '');
      return textContent.length > 100 ? `${textContent.substring(0, 100)}...` : textContent;
    });

    const loadEditor = () => {
      if (!isLoaded.value) {
        isLoaded.value = true;
        // Nettoyer l'observer une fois chargé
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }
    };

    const setupIntersectionObserver = () => {
      if (!containerRef.value || typeof IntersectionObserver === 'undefined') {
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= props.intersectionThreshold) {
              loadEditor();
            }
          });
        },
        {
          threshold: props.intersectionThreshold,
          // Ajouter une marge pour commencer le chargement un peu avant que l'élément soit visible
          rootMargin: '50px',
        }
      );

      observer.observe(containerRef.value);
    };

    onMounted(() => {
      if (props.forceLoad || props.loadMode === 'immediate') {
        loadEditor();
      } else if (props.loadMode === 'auto') {
        // Utiliser nextTick pour s'assurer que le DOM est prêt
        nextTick(() => {
          setupIntersectionObserver();
        });
      }
      // Si loadMode === 'click', on ne fait rien, le chargement se fera au clic
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    });

    // Référence vers le composant chargé
    const baseComponentRef = ref();

    // Méthode focus qui redirige vers le composant chargé
    const focus = () => {
      if (isLoaded.value && baseComponentRef.value && baseComponentRef.value.focus) {
        baseComponentRef.value.focus();
      } else if (!isLoaded.value) {
        // Si pas encore chargé, charger d'abord puis focus
        loadEditor();
        nextTick(() => {
          if (baseComponentRef.value && baseComponentRef.value.focus) {
            baseComponentRef.value.focus();
          }
        });
      }
    };

    // Méthodes pour contrôler l'Intersection Observer pendant le scroll
    const pauseObserver = () => {
      if (observer) {
        observer.disconnect();
      }
    };

    const resumeObserver = () => {
      if (!isLoaded.value && containerRef.value) {
        setupIntersectionObserver();
      }
    };

    return {
      containerRef,
      isLoaded,
      truncatedContent,
      loadEditor,
      baseComponentRef,
      focus, // Exposer focus via return
      pauseObserver, // Exposer pour contrôler l'observer
      resumeObserver, // Exposer pour contrôler l'observer
    };
  },
});
</script>

<style scoped lang="scss">
.lazy-wysiwyg-container {
  min-height: 120px;
  transition: all 0.3s ease;

  &.is-loading {
    border: 2px dashed #e0e0e0;
    border-radius: 4px;
  }

  &.is-loaded {
    border: none;
  }
}

.lazy-wysiwyg-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 1rem;
  cursor: pointer;
  background: #fafafa;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
    border-color: #ccc;
  }

  .placeholder-content {
    text-align: center;
    max-width: 100%;
  }

  .placeholder-icon {
    font-size: 2rem;
    color: #999;
    margin-bottom: 0.5rem;
  }

  .placeholder-text {
    color: #666;
    font-size: 0.9rem;
    margin: 0 0 1rem 0;
    font-style: italic;
  }

  .placeholder-preview {
    max-width: 100%;
    margin-top: 0.5rem;
    
    .preview-content {
      background: white;
      border: 1px solid #ddd;
      border-radius: 3px;
      padding: 0.5rem;
      font-size: 0.85rem;
      color: #555;
      text-align: left;
      max-height: 60px;
      overflow: hidden;
      line-height: 1.3;
    }
  }
}
</style>
